import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  Table,
} from "reactstrap";
import classnames from "classnames";
import Select from "react-select";
import { getBranchFares, postBranchFares } from "../../../helpers/backend_helper";
import { Alert, ConfirmAlertHelper } from "../../../helpers/alerts/alertHelper";

/*
  Matriz de precios zona origen -> zona destino del ramal, para el modo de tarifa
  "por distancia" de la boletera.

  El recorrido se parte en dos tramos con la geocerca intermedia, que pertenece a ambos:
  ida = primera..intermedia, regreso = intermedia..ultima. Un selector Ida / Regreso elige cual se ve.
  Dentro de cada tramo solo se cobra hacia adelante: las zonas llegan del backend en el
  orden en que el ramal las recorre, y un trayecto vale de una zona a ella misma o a las
  que vienen despues. El precio no guarda tramo: si un par aparece en los dos
  (tipicamente la diagonal A->A) lo comparten. Se captura como matriz: origen en las filas, destino en las
  columnas y el precio en la interseccion. La diagonal es el precio dentro de la misma
  zona; debajo de ella (sentido inverso) las celdas quedan vacias porque no se venden.

  Hay una pestaña por tipo de tarifa. La matriz de todos los tipos vive en memoria
  mientras se edita —con clave plana "origen-destino-tipo"— para que cambiar de pestaña
  no pierda lo capturado, y se guarda entera de una sola vez.

  Un campo vacio significa "sin tarifa": ese trayecto no se puede vender. Es distinto de
  un 0, que es un trayecto gratuito. El backend respeta esa diferencia.
*/
const clave = (origenId, destinoId, tipoId) => `${origenId}-${destinoId}-${tipoId}`;

// Pares [origen, destino] hacia adelante en una secuencia de zonas, diagonal incluida
const paresAdelante = (zonas) => zonas.flatMap((o, i) => zonas.slice(i).map((d) => [o.id, d.id]));

const TRAMOS_VACIOS = { ida: [], regreso: [] };

const ColorZona = ({ zona }) => (
  <span
    className="d-inline-block me-2"
    style={{
      width: 12,
      height: 12,
      borderRadius: 4,
      background: zona.color || "#ced4da",
    }}
  />
);

const PreciosModal = ({ modal, setModal }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(false);
  const [tramos, setTramos] = useState(TRAMOS_VACIOS);
  const [tramo, setTramo] = useState("ida");
  const [tipos, setTipos] = useState([]);
  const [matriz, setMatriz] = useState({});
  const [tipoId, setTipoId] = useState(null);
  const [tipoDestino, setTipoDestino] = useState(null);

  /*
    Aplana la matriz anidada del backend a la clave compuesta que usa la edicion. Descarta
    lo que no sea hacia adelante en ningun tramo (precios viejos en sentido inverso o de
    zonas que el ramal ya no usa): no se muestran y al guardar el backend los borra.
  */
  const aplanar = (anidada, tramosRamal) => {
    const validos = new Set(
      [...paresAdelante(tramosRamal.ida), ...paresAdelante(tramosRamal.regreso)].map(
        ([o, d]) => `${o}-${d}`
      )
    );
    const plana = {};
    Object.entries(anidada ?? {}).forEach(([origen, destinos]) => {
      Object.entries(destinos ?? {}).forEach(([destino, precios]) => {
        if (!validos.has(`${origen}-${destino}`)) return;
        Object.entries(precios ?? {}).forEach(([tipo, precio]) => {
          plana[clave(origen, destino, tipo)] = String(precio);
        });
      });
    });
    return plana;
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBranchFares(modal.id, { withToast: false });
      if (response?.status) {
        const tramosRamal = {
          ida: response.zonas_ida ?? [],
          regreso: response.zonas_regreso ?? [],
        };
        setTramos(tramosRamal);
        setTramo("ida");
        setTipos(response.tipos ?? []);
        setMatriz(aplanar(response.matriz, tramosRamal));
        setTipoId(response.tipos?.[0]?.id ?? null);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  }, [modal.id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const zonas = tramos[tramo];
  const sinZonas = tramos.ida.length === 0 && tramos.regreso.length === 0;

  const handleClose = () => setModal({ ...modal, show: false, id: null });

  const setPrecio = (origenId, destinoId, valor) => {
    setMatriz((prev) => ({ ...prev, [clave(origenId, destinoId, tipoId)]: valor }));
    setEdit(true);
  };

  const precioDe = (origenId, destinoId) => matriz[clave(origenId, destinoId, tipoId)] ?? "";

  const capturadosDe = (id) =>
    Object.entries(matriz).filter(([k, v]) => k.split("-")[2] == id && (v ?? "") !== "").length;

  // Los atajos actuan solo sobre el tipo activo y avisan antes de pisar datos
  const hayCapturado = (pares) => pares.some((k) => (matriz[k] ?? "") !== "");

  const confirmarYAplicar = (afectadas, titulo, texto, boton, aplicar) => {
    if (!hayCapturado(afectadas)) {
      aplicar();
      setEdit(true);
      return;
    }

    ConfirmAlertHelper(
      (confirmed) => {
        if (!confirmed) return;
        aplicar();
        setEdit(true);
      },
      titulo,
      texto,
      boton
    );
  };

  // Deja el tipo elegido identico al activo, vacios incluidos
  const copiarTipo = () => {
    if (!tipoDestino) {
      Alert("Elige el tipo de tarifa al que se copiarán los precios.", false);
      return;
    }

    // Ambos tramos, sin repetir los pares que comparten, para que el tipo quede identico
    const celdas = [
      ...new Map(
        [...paresAdelante(tramos.ida), ...paresAdelante(tramos.regreso)].map((par) => [
          par.join("-"),
          par,
        ])
      ).values(),
    ];
    const tipoNombre = tipos.find((t) => t.id === tipoDestino)?.nombre;

    confirmarYAplicar(
      celdas.map(([o, d]) => clave(o, d, tipoDestino)),
      "Copiar tipo de tarifa",
      `Se reemplazarán todos los precios de ${tipoNombre} por los de esta pestaña. ¿Continuar?`,
      "Copiar",
      () =>
        setMatriz((prev) => {
          const next = { ...prev };
          celdas.forEach(
            ([o, d]) => (next[clave(o, d, tipoDestino)] = prev[clave(o, d, tipoId)] ?? "")
          );
          return next;
        })
    );
  };

  const guardar = async () => {
    setSaving(true);
    try {
      // Se rearma la forma anidada que espera el backend, descartando vacios
      const anidada = {};
      Object.entries(matriz).forEach(([k, valor]) => {
        if (valor === "" || valor === null || valor === undefined) return;
        const [origen, destino, tipo] = k.split("-");
        anidada[origen] = anidada[origen] ?? {};
        anidada[origen][destino] = anidada[origen][destino] ?? {};
        anidada[origen][destino][tipo] = valor;
      });

      const response = await postBranchFares(modal.id, { matriz: anidada });
      if (response?.status) {
        setMatriz(aplanar(response.matriz, tramos));
        setEdit(false);
      }
    } catch (error) {
      console.error(error);
      Alert("No se pudieron guardar los precios.", false);
    }
    setSaving(false);
  };

  const otrosTipos = tipos.filter((t) => t.id !== tipoId);

  return (
    <div>
      <Modal isOpen={modal.show} className="modal-dialog modal-xl" role="document">
        <ModalHeader className="bg-primary" toggle={handleClose}>
          Ramales - Precios por zona
        </ModalHeader>
        <ModalBody>
          {loading && (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          )}

          {!loading && sinZonas && (
            <div className="alert alert-warning mb-0">
              Este ramal no tiene zonas, así que no hay trayectos que cobrar. Asigna zonas a las
              geocercas del ramal (botón Geocercas) para capturar precios.
            </div>
          )}

          {!loading && !sinZonas && (
            <>
              <div className="overflow-auto">
                <Nav tabs className="nav-tabs-custom flex-nowrap mb-3">
                  {tipos.map((t) => (
                    <NavItem key={t.id}>
                      <NavLink
                        href="#"
                        className={classnames({ active: tipoId === t.id }, "px-3 text-nowrap")}
                        onClick={(e) => {
                          e.preventDefault();
                          setTipoId(t.id);
                          setTipoDestino(null);
                        }}
                      >
                        {t.nombre}
                        <span className="badge bg-light text-muted ms-2">{capturadosDe(t.id)}</span>
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </div>

              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <div
                  className="d-inline-flex p-1 rounded-3"
                  style={{ background: "#eff2f7" }}
                  title={
                    tramos.regreso.length === 0
                      ? "El ramal no tiene geocerca intermedia: todo el recorrido es ida."
                      : ""
                  }
                >
                  {[
                    { key: "ida", label: "Ida" },
                    { key: "regreso", label: "Regreso" },
                  ].map((opcion) => (
                    <button
                      key={opcion.key}
                      type="button"
                      className={classnames(
                        "btn btn-sm border-0 rounded-3 px-3 fw-semibold",
                        tramo === opcion.key ? "bg-white shadow-sm text-dark" : "text-muted"
                      )}
                      disabled={opcion.key === "regreso" && tramos.regreso.length === 0}
                      onClick={() => setTramo(opcion.key)}
                    >
                      {opcion.label}
                    </button>
                  ))}
                </div>
                {otrosTipos.length > 0 && (
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ minWidth: 180 }}>
                      <Select
                        placeholder="Copiar a…"
                        options={otrosTipos}
                        value={otrosTipos.find((t) => t.id === tipoDestino) ?? null}
                        getOptionLabel={(o) => o.nombre}
                        getOptionValue={(o) => `${o.id}`}
                        onChange={(option) => setTipoDestino(option?.id ?? null)}
                        isClearable
                      />
                    </div>
                    <Button color="light" size="sm" onClick={copiarTipo}>
                      <i className="fa fa-copy"></i> Copiar tipo
                    </Button>
                  </div>
                )}
              </div>

              {zonas.length === 0 && (
                <div className="alert alert-light mb-2">
                  Las geocercas de este tramo no tienen zonas asignadas.
                </div>
              )}

              {zonas.length > 0 && (
                <Table responsive bordered size="sm" className="align-middle mb-2">
                  <thead>
                    <tr>
                      <th
                        className="bg-white"
                        style={{ minWidth: 160, position: "sticky", left: 0, zIndex: 1 }}
                      >
                        <small className="text-muted">Origen \ Destino</small>
                      </th>
                      {zonas.map((destino) => (
                        <th key={destino.id} className="text-center text-nowrap">
                          <ColorZona zona={destino} />
                          {destino.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {zonas.map((origen, i) => (
                      <tr key={origen.id}>
                        <th
                          className="bg-white text-nowrap"
                          style={{ position: "sticky", left: 0, zIndex: 1 }}
                        >
                          <ColorZona zona={origen} />
                          {origen.name}
                        </th>
                        {zonas.map((destino, j) =>
                          j < i ? (
                            // Sentido inverso: no se vende
                            <td key={destino.id} className="bg-light" />
                          ) : (
                            <td
                              key={destino.id}
                              className={i === j ? "table-light" : ""}
                              title={`${origen.name} → ${destino.name}`}
                            >
                              <input
                                className="form-control form-control-sm"
                                style={{ minWidth: 70 }}
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="—"
                                value={precioDe(origen.id, destino.id)}
                                onChange={(e) => setPrecio(origen.id, destino.id, e.target.value)}
                              />
                            </td>
                          )
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <small className="text-muted">
                Filas = origen, columnas = destino, en el orden en que el ramal recorre las zonas.
                Ida: de la primera geocerca a la intermedia; regreso: de la intermedia a la última.
                Solo se venden trayectos hacia adelante (de la diagonal hacia arriba). Un campo
                vacío significa que ese trayecto no se puede vender. Un 0 es un trayecto gratuito.
              </small>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {edit && (
            <Button color="primary" onClick={guardar} disabled={saving}>
              {saving ? <Spinner size="sm" /> : <i className="fa fa-save"></i>} Guardar Cambios
            </Button>
          )}
          <Button color="secondary" onClick={handleClose}>
            <i className="fa fa-times"></i> Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PreciosModal;
